// src/live/utils/withTimeout.ts
// Shared timeout wrapper — single source of truth for the live tab.

/**
 * Race a promise against a timeout.
 *
 * @param promise  The promise to wrap.
 * @param ms       Timeout duration in milliseconds.
 * @param label    Optional label included in the timeout error message.
 * @returns        The resolved value of `promise`, or rejects with a timeout Error.
 *
 * The internal timer is always cleaned up — whether the promise resolves,
 * rejects, or the timeout fires first.
 */
export const withTimeout = async <T>(
  promise: Promise<T>,
  ms: number,
  label?: string,
): Promise<T> => {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const timeoutPromise = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(
      () => reject(new Error(label ?? `Timed out after ${ms}ms`)),
      ms,
    );
  });

  try {
    return await Promise.race([promise, timeoutPromise]);
  } finally {
    if (timeoutId !== undefined) clearTimeout(timeoutId);
  }
};
